import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { bookingAPI } from '../services/api';
import { COURT_NUMBERS } from '../utils/constants';

dayjs.locale('en-gb');

const AvailabilityCalendar = () => {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedCourt, setSelectedCourt] = useState(1);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAvailability = async () => {
        if (!selectedDate || !selectedCourt) return;
        
        setLoading(true);
        setError(null);
        try {
            const response = await bookingAPI.getAvailableSlots(
                selectedDate.format('YYYY-MM-DD'),
                selectedCourt
            );
            
            console.log('Full API Response:', response);
            
            // Handle different possible response structures
            let slots = [];
            if (response && response.data) {
                // If response.data is an array
                if (Array.isArray(response.data)) {
                    slots = response.data;
                }
                // If response.data has a nested array property
                else if (response.data.timeSlots && Array.isArray(response.data.timeSlots)) {
                    slots = response.data.timeSlots;
                }
                // If response itself is an array
                else if (Array.isArray(response)) {
                    slots = response;
                }
            }
            
            console.log('Processed slots:', slots);
            setAvailableSlots(slots);
            
        } catch (error) {
            console.error('Failed to fetch availability:', error);
            setError('Failed to fetch availability');
            setAvailableSlots([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAvailability();
    }, [selectedDate, selectedCourt]);

    const renderTimeSlot = (slot, index) => {
        // Ensure we have a valid slot object
        if (!slot || typeof slot !== 'object') {
            console.warn(`Invalid slot at index ${index}:`, slot);
            return null;
        }

        // Extract properties safely
        const displayName = slot.displayName || `${slot.startTime || ''} - ${slot.endTime || ''}`;
        const price = slot.price || 0;
        const isAvailable = slot.isAvailable !== false; // Default to true if undefined

        return (
            <Chip
                key={`slot-${index}`}
                label={`${displayName} - Rs ${price}`}
                color={isAvailable ? "success" : "error"}
                variant="outlined"
                sx={{ mb: 1, mr: 1 }}
            />
        );
    };

    return (
        <Paper elevation={3} sx={{ p: 3, m: 2 }}>
            <Typography variant="h5" gutterBottom color="primary">
                Court Availability
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Select Date"
                            value={selectedDate}
                            onChange={setSelectedDate}
                            disablePast
                            format="DD/MM/YYYY"
                            slotProps={{
                                popper: {
                                    sx: {
                                        '& .MuiPaper-root': {
                                            backgroundColor: '#2c387e',
                                            border: '2px solid #c4d835',
                                            borderRadius: '16px',
                                            boxShadow: '0 8px 32px rgba(44, 56, 126, 0.6)',
                                        },
                                        '& .MuiPickersCalendarHeader-root': {
                                            backgroundColor: '#3f51b5',
                                            color: '#c4d835',
                                            '& .MuiPickersArrowSwitcher-root': {
                                                '& .MuiIconButton-root': {
                                                    color: '#c4d835',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(196, 216, 53, 0.2)',
                                                    }
                                                }
                                            },
                                            '& .MuiPickersCalendarHeader-label': {
                                                color: '#c4d835',
                                                fontWeight: 700,
                                            }
                                        },
                                        '& .MuiDayCalendar-header': {
                                            '& .MuiTypography-root': {
                                                color: '#d4e157',
                                                fontWeight: 600,
                                            }
                                        },
                                        // Target ALL day elements with higher specificity
                                        '& .MuiPickersDay-root, & .MuiDayCalendar-weekDayLabel, & .MuiPickersDay-root.MuiButtonBase-root': {
                                            color: '#c4d835 !important',
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: 'rgba(196, 216, 53, 0.2) !important',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'transparent !important',
                                                color: '#c4d835 !important',
                                                fontWeight: 700,
                                                borderRadius: '50% !important',
                                                border: '2px solid #c4d835 !important',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(196, 216, 53, 0.2) !important',
                                                    color: '#c4d835 !important',
                                                    borderRadius: '50% !important',
                                                    border: '2px solid #c4d835 !important',
                                                }
                                            },
                                            '&.MuiPickersDay-today': {
                                                border: '2px solid #d4e157 !important',
                                                backgroundColor: 'transparent !important',
                                                color: '#d4e157 !important',
                                            },
                                            '&.Mui-disabled': {
                                                color: 'rgba(196, 216, 53, 0.4) !important',
                                            },
                                            // Override any other potential color classes
                                            '&.MuiButtonBase-root': {
                                                color: '#c4d835 !important',
                                            },
                                            '&.MuiPickersDay-root': {
                                                color: '#c4d835 !important',
                                            }
                                        },
                                        // Additional specificity for disabled dates
                                        '& .MuiPickersDay-root.Mui-disabled': {
                                            color: 'rgba(196, 216, 53, 0.4) !important',
                                        },
                                        // Override any default text colors
                                        '& .MuiTypography-root': {
                                            color: '#c4d835 !important',
                                        },
                                        // Ensure all text elements in the calendar are yellow
                                        '& *': {
                                            color: '#c4d835 !important',
                                        },
                                                                                 // Year selection styling
                                         '& .MuiYearCalendar-root': {
                                             '& .MuiPickersYear-yearButton': {
                                                 color: '#c4d835 !important',
                                                 fontWeight: 500,
                                                 '&:hover': {
                                                     backgroundColor: 'rgba(196, 216, 53, 0.2) !important',
                                                     color: '#c4d835 !important',
                                                 },
                                                 '&.Mui-selected': {
                                                     backgroundColor: '#c4d835 !important',
                                                     color: '#2c387e !important',
                                                     fontWeight: 700,
                                                     borderRadius: '8px !important',
                                                     '&:hover': {
                                                         backgroundColor: '#d4e157 !important',
                                                         color: '#2c387e !important',
                                                         borderRadius: '8px !important',
                                                     }
                                                 }
                                             }
                                         },
                                        // Month selection styling
                                        '& .MuiMonthCalendar-root': {
                                            '& .MuiPickersMonth-monthButton': {
                                                color: '#c4d835 !important',
                                                fontWeight: 500,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(196, 216, 53, 0.2) !important',
                                                    color: '#c4d835 !important',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#c4d835 !important',
                                                    color: '#2c387e !important',
                                                    fontWeight: 700,
                                                    '&:hover': {
                                                        backgroundColor: '#d4e157 !important',
                                                        color: '#2c387e !important',
                                                    }
                                                }
                                            }
                                        },
                                        // Additional overrides for any remaining elements
                                        '& .MuiButtonBase-root': {
                                            color: '#c4d835 !important',
                                            '&:hover': {
                                                backgroundColor: 'rgba(196, 216, 53, 0.2) !important',
                                            }
                                        },
                                        '& .MuiPickersYear-root': {
                                            '& .MuiButtonBase-root': {
                                                color: '#c4d835 !important',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(196, 216, 53, 0.2) !important',
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                            sx={{ width: '100%' }}
                        />
                    </LocalizationProvider>
                </Grid>

                {/* <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Court Number</InputLabel>
                        <Select
                            value={selectedCourt}
                            onChange={(e) => setSelectedCourt(e.target.value)}
                            label="Court Number"
                        >
                            {COURT_NUMBERS.map(num => (
                                <MenuItem key={num} value={num}>
                                    Court {num}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid> */}
            </Grid>

            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Available Time Slots on {selectedDate.format('MMMM DD, YYYY')}
                    </Typography>

                    {loading ? (
                        <Typography>Loading availability...</Typography>
                    ) : error ? (
                        <Typography color="error">{error}</Typography>
                    ) : Array.isArray(availableSlots) && availableSlots.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {availableSlots.map((slot, index) => renderTimeSlot(slot, index))}
                        </Box>
                    ) : (
                        <Typography color="error">
                            No available slots for the selected date and court.
                        </Typography>
                    )}
                    
                 
                </CardContent>
            </Card>
        </Paper>
    );
};

export default AvailabilityCalendar;